import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DynamicFormControlService } from '../../dynamic-form-control.service';
import { DateTimeControl } from '../../form-models/datetime-control';
import { FormControlBase } from '../../form-models/form-control-base';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'dynamic-form',
  templateUrl: './dynamic-form.component.html',
  providers: [DynamicFormControlService],
  styleUrls: ['./dynamic-form.component.css'],
})
export class DynamicFormComponent implements OnDestroy, OnInit, OnChanges {
  valueChangesSub: Subscription;
  _controls: FormControlBase<any>[] = [];
  private unsubscribe: Subject<boolean> = new Subject();

  groupNames: string[] = [];
  @Input() separateByGroups: boolean = false;
  @Input() separateGroupsByAccordion: boolean = false;
  activeIndex: number | number[]=[];

  get controls(): FormControlBase<any>[] {
    return this._controls;
  }

  @Input() set controls(value: FormControlBase<any>[]) {
    this._controls = value;
    this.createForm();
  }

  private _isReadOnly: boolean;

  get isReadOnly(): boolean {
    return this._isReadOnly;
  }

  public ngOnChanges(changes: SimpleChanges){
    if(changes['separateByGroups'] && changes['separateByGroups']['currentValue']){
      const value = this._controls;

      this.groupNames = value && value.length > 0 ? [...Array.from(new Set(value.map(e => e.groupName || '')))] : [];
    }
  }

  @Input() set isReadOnly(value: boolean) {
    this._isReadOnly = value;

    if (this.form) {
      this.setFormEnabling();
      this.updateControlDisabledProperty();
      this.updateDatetimControlDisabledProperty();
    }
  }

  @Output() formValidChange = new EventEmitter();
  @Output() formDirtyChange = new EventEmitter();
  @Output() form: FormGroup = new FormGroup({});

  @Output() formGroupCreated = new EventEmitter<FormGroup>();

  @Output() onClickRaised: EventEmitter<any> = new EventEmitter<any>();
  @Output() onDropdownChangeRaised: EventEmitter<any> = new EventEmitter<any>();
  @Output() onOverrideActionExecuted: EventEmitter<any> = new EventEmitter<any>();

  constructor(private formControlService: DynamicFormControlService) {}

  ngOnInit(): void {
    const index=[];

    setTimeout(()=>{
      this._controls.forEach(e=>e.value?index.push(this.groupNames.indexOf(e.groupName)):'');
      this.activeIndex=[...Array.from(new Set(index))];
    },100);
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next(true);
    this.unsubscribe.complete();
  }

  public onClickEvent(): void {
    this.onClickRaised.emit();
  }

  public onOverrideActionExecutedRaised(event: any): void {
    this.onOverrideActionExecuted.emit(event);
  }

  private setFormEnabling(): void {
    if (this._isReadOnly) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }
  }

  private createForm(): void {
    this.form = this.formControlService.toFormGroup(this.controls);

    if (this.isReadOnly) {
      this.form.disable();
    }

    this.formGroupCreated.emit(this.form);

    // Allow forms with no required fields to be submitted straight away
    if (this.form.valid) {
      this.formValidChange.emit(true);
    }

    this.form.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe(() => this.formValidChange.emit(this.form.valid));

    this.form.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
      if (!this.form.pristine && !this.form.disabled) {
        this.formDirtyChange.emit(this.form.dirty);
      }
    });
  }

  public onDropdownChange(event: any): void {
    this.onDropdownChangeRaised.emit(event);
  }

  private updateDatetimControlDisabledProperty(): void {
    if (this.controls && Object.values(this.controls).some(e => e instanceof DateTimeControl)) {
      Object.values(this.controls)
        .filter(e => e instanceof DateTimeControl)
        .forEach(e => {
          if (this._isReadOnly || this._isReadOnly === false) {
            e.disabled = !e.disabled ? this._isReadOnly : e.disabled;
          }
        });
    }
  }

  private updateControlDisabledProperty(): void {
    if (this.controls && Object.values(this.controls)) {
      Object.values(this.controls)
        .filter(e => !(e instanceof DateTimeControl))
        .forEach(e => {
          if(e.displayLockedAttribute){
            e.disabled = e.disabled;
          }
        });
    }
  }
}
